import { type DayConfig, dayConfigs } from '@/config/DayConfig';
import { npcConfigs } from '@/config/NPCConfig';
import type { Player } from '@/entities';
import { NPC } from '@/entities';
import { SoundManager } from '@/systems/SoundManager';
import type { DialogChoice, DialogTree } from '@/types';
import {getItemConfig, ItemConfig} from "@/config/ItemsConfig.ts";
import {Game} from "@/core";
import {AssetManager} from "@/managers";

// Interface for tracking NPC interaction states
export interface NPCInteractionState {
  hasInteracted: boolean;
  lastDialogNodeId?: string;
  lastSpeaker?: string;
  lastText?: string;
  lastCharacterImage?: string;
  lastBackgroundImage?: string;
  lastIsEnd?: boolean;
  lastChoices?: Array<{
    text: string;
    wasSelected: boolean;
  }>;
  interactionCount: number;
  firstInteractionTime?: number;
  lastInteractionTime?: number;
  isRepeatable?: boolean; // Flag to indicate if this NPC should have repeatable dialogs
}

export enum CUSTOM_VALUES {
  general_karma = 'general_karma', //RANGE -6 to 6
  bunga_affinity = 'bunga_affinity', //RANGE -4 to 4
  holga_affinity = 'holga_affinity', //RANGE -2 to 3
  ato_affinity = 'ato_affinity', //RANGE 0 to 2
  elsa_affinity = 'elsa_affinity', //RANGE -3 to 7

  bunga_elsa_affinity = 'bunga_elsa_affinity',
  holga_elsa_affinity = 'holga_elsa_affinity',
  ato_elsa_affinity = 'ato_elsa_affinity',
  elsa_bunga_affinity = 'elsa_bunga_affinity',
  false_arguments = 'false_arguments',
}

export enum CUSTOM_GAME_STATE {
  BONEKA_GIVEN = 'BONEKA_GIVEN',
  ATO_HAPPY = 'ATO_HAPPY',
  HOLGA_FOUND_MORNING = 'HOLGA_FOUND_MORNING',
  ROD_USED = 'ROD_USED',
  DIANA_BIRTHDAY_HAPPY = 'DIANA_BIRTHDAY_HAPPY',
  EVIDENCE_CLOTH_GATE_FOUND = 'EVIDENCE_CLOTH_GATE_FOUND',
  BUNGA_BATH = 'BUNGA_BATH',
  HOLGA_PANADA_POISONED = 'HOLGA_PANADA_POISONED',

  BUNGA_NOT_ON_SONDAKH_DEATH = 'BUNGA_NOT_ON_SONDAKH_DEATH',
  BUNGA_ON_SONDAKH_DEATH = 'BUNGA_ON_SONDAKH_DEATH',
  YAN_PANADA_POISON = 'YAN_PANADA_POISON',
  BEAR_RETRIEVED = 'BEAR_RETRIEVED',
  MONEY_TAKEN = 'MONEY_TAKEN',
  MONEY_NOT_TAKEN = 'MONEY_NOT_TAKEN',
  BUNGA_NOT_WITH_ELSA = 'BUNGA_NOT_WITH_ELSA',
  ATO_UNFRIENDLY = 'ATO_UNFRIENDLY',
  ELSA_ON_STAKE = 'ELSA_ON_STAKE',
  BUNGA_WITH_ELSA = 'BUNGA_WITH_ELSA',
  ELSA_NOT_ON_STAKE = 'ELSA_NOT_ON_STAKE',
  ARGUMENT_DIANA_WITH_ELSA_CHOSEN = 'ARGUMENT_DIANA_WITH_ELSA_CHOSEN',
  ARGUMENT_BUNGA_SAW_EVERYTHING = 'ARGUMENT_BUNGA_SAW_EVERYTHING',
  ARGUMENT_DEAD_HOURS_AGO = 'ARGUMENT_DEAD_HOURS_AGO',
  ARGUMENT_TIMUR_CHOSEN = 'ARGUMENT_TIMUR_CHOSEN',
  ARGUMENT_UTARA_CHOSEN = 'ARGUMENT_UTARA_CHOSEN',
  DONATED_TO_CHURCH = 'DONATED_TO_CHURCH',

  ENDING_S_SECRET_ENDING = 'ENDING_S_SECRET_ENDING',
  ENDING_A_GOOD_ENDING = 'ENDING_A_GOOD_ENDING',
  ENDING_B_STILL_SURVIVE_ENDING = 'ENDING_B_BAD_ENDING',
  ENDING_F_GAME_OVER_ENDING = 'ENDING_F_GAME_OVER_ENDING',
  ENDING_D_FAIL_SELF_OR_ELSA_ENDING = 'ENDING_D_FAIL_SELF_OR_ELSA_ENDING',

}

export class StoryManager {

  private static instance: StoryManager;
  private affection: number = 0;
  private hostility: number = 0;
  private eventListeners: Map<string, ((value: number) => void)[]> = new Map();
  private player: Player | null = null; // Reference to the player object

  private gameStates:string[] = []
  private customValues:any = {}

  private startTime;

  // Track NPC interaction states
  private npcInteractionStates: Map<string, NPCInteractionState> = new Map();

  // Track special dialogs unlocked (for winning condition)
  private unlockedSpecialDialogs: Set<string> = new Set();

  // Track NPCs with repeatable dialogs (populated from NPC configs)
  private repeatableNPCs: Set<string> = new Set();

  // Manage NPCs and day system
  private npcs: NPC[] = [];
  private currentDay: number = 1;
  private dayEventListeners: Map<string, ((day: number) => void)[]> = new Map();

  // Track whether we've shown the initial dialog for the current day
  private dayDialogShown: Set<number> = new Set();

  // Inventory system - Map of itemId to quantity
  private inventory: Map<string, number> = new Map();
  private inventoryEventListeners: Map<string, ((item?:ItemConfig) => void)[]> = new Map();

  // Global Discovered Items
  private discoveredItems: Set<string> = new Set();
  private readonly DISCOVERED_ITEMS_KEY = 'discovered_items';

  // Items discovered in the current playthrough
  private itemsDiscoveredThisRun: Set<string> = new Set();

  private constructor() {
    console.log(
      `StoryManager initialized. Starting affection: ${this.affection}, hostility: ${this.hostility}`,
    );

    this.startTime = performance.now();

    this.loadDiscoveredItems();
    this.loadDayAsset(1);
    this.loadDayAsset(2);
  }

  public static getInstance(): StoryManager {
    if (!StoryManager.instance) {
      StoryManager.instance = new StoryManager();
    }
    return StoryManager.instance;
  }

  public addGameState(state:string){
    this.gameStates.push(state)
    console.log("GAMESTATES", this.gameStates)
    return
  }

  public checkStateExists(state:string):boolean{
    return this.gameStates.includes(state);
  }

  public removeGameState(state:string){
    let idx = this.gameStates.findIndex((obj)=>obj===state)
    if(idx > -1){
      this.gameStates.splice(idx, 1);
    }
  }

  public checkCustomValue(key:string):number{
    return this.customValues[key] ? this.customValues[key] : 0
  }

  public addCustomValue(key:string, value:number){
    if(this.customValues[key]){
      this.customValues[key] = this.customValues[key] + value
    }else{
      this.customValues[key] = value
    }

    if(this.customValues["general_karma"] < 0){
      Game.getInstance().setVignetteDisplayBPM(3)
    }

    if(this.customValues["general_karma"] < -1){
      Game.getInstance().setVignetteDisplayBPM(5)
    }

    if(this.customValues["general_karma"] < -2){
      Game.getInstance().setVignetteDisplayBPM(7)
    }

    if(this.customValues["false_arguments"] > 0){
      Game.getInstance().setVignetteDisplayBPM(10)
    }

    if(this.customValues["false_arguments"] > 1){
      Game.getInstance().setVignetteDisplayBPM(20)
      const soundManager = SoundManager.getInstance();
      soundManager.playMusic(`music_tense`);
    }

    if(this.customValues["false_arguments"] > 2){
      const soundManager = SoundManager.getInstance();
      soundManager.playMusic(`music_super_tense`);
      Game.getInstance().setVignetteDisplayBPM(50)
    }

  }

  public addPoints(amount: number): void {
    if (amount > 0) {
      this.affection += amount;
      console.log(`Affection added: +${amount}. Total affection: ${this.affection}`);
      this.notifyListeners('affectionChanged', this.affection);
      this.notifyListeners('affectionAdded', amount);
    } else if (amount < 0) {
      this.hostility += Math.abs(amount);
      console.log(`Hostility added: +${Math.abs(amount)}. Total hostility: ${this.hostility}`);
      this.notifyListeners('hostilityChanged', this.hostility);
      this.notifyListeners('hostilityAdded', Math.abs(amount));
    }
    // For backward compatibility, also notify about total points change
    this.notifyListeners('pointsChanged', this.getTotalPoints());
    this.notifyListeners('pointsAdded', amount);
  }

  public deductPoints(amount: number): void {
    if (amount > 0) {
      this.hostility += amount;
      console.log(`Hostility added: +${amount}. Total hostility: ${this.hostility}`);
      this.notifyListeners('hostilityChanged', this.hostility);
      this.notifyListeners('hostilityAdded', amount);
    } else if (amount < 0) {
      const affectionToDeduct = Math.abs(amount);
      this.affection = Math.max(0, this.affection - affectionToDeduct);
      console.log(`Affection deducted: -${affectionToDeduct}. Total affection: ${this.affection}`);
      this.notifyListeners('affectionChanged', this.affection);
      this.notifyListeners('affectionDeducted', affectionToDeduct);
    }
    // For backward compatibility, also notify about total points change
    this.notifyListeners('pointsChanged', this.getTotalPoints());
    this.notifyListeners('pointsDeducted', amount);
  }

  // New methods for getting individual values
  public getAffection(): number {
    return this.affection;
  }

  public getHostility(): number {
    return this.hostility;
  }

  // Updated method for backward compatibility
  public getPoints(): number {
    return this.getTotalPoints();
  }

  // Calculate total points as affection minus hostility
  public getTotalPoints(): number {
    return this.affection - this.hostility;
  }

  public setPoints(amount: number): void {
    // Reset both values and distribute the amount
    this.affection = Math.max(0, amount);
    this.hostility = Math.max(0, -amount);
    console.log(
      `Points set to: ${amount}. Affection: ${this.affection}, Hostility: ${this.hostility}`,
    );
    this.notifyListeners('affectionChanged', this.affection);
    this.notifyListeners('hostilityChanged', this.hostility);
    this.notifyListeners('pointsChanged', this.getTotalPoints());
  }

  // New methods for setting individual values
  public setAffection(amount: number): void {
    this.affection = Math.max(0, amount);
    console.log(`Affection set to: ${this.affection}`);
    this.notifyListeners('affectionChanged', this.affection);
    this.notifyListeners('pointsChanged', this.getTotalPoints());
  }

  public setHostility(amount: number): void {
    this.hostility = Math.max(0, amount);
    console.log(`Hostility set to: ${this.hostility}`);
    this.notifyListeners('hostilityChanged', this.hostility);
    this.notifyListeners('pointsChanged', this.getTotalPoints());
  }

  // Event system for UI updates
  public addEventListener(event: string, callback: (value: number) => void): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.push(callback);
    }
  }

  public removeEventListener(event: string, callback: (value: number) => void): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private notifyListeners(event: string, value: number): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        callback(value);
      });
    }
  }

  // Story milestone checks
  public hasReachedMilestone(threshold: number): boolean {
    return this.getTotalPoints() >= threshold;
  }

  public isInRange(min: number, max: number): boolean {
    const totalPoints = this.getTotalPoints();
    return totalPoints >= min && totalPoints <= max;
  }

  // New milestone checks for individual values
  public hasReachedAffectionMilestone(threshold: number): boolean {
    return this.affection >= threshold;
  }

  public hasReachedHostilityMilestone(threshold: number): boolean {
    return this.hostility >= threshold;
  }

  public isAffectionInRange(min: number, max: number): boolean {
    return this.affection >= min && this.affection <= max;
  }

  public isHostilityInRange(min: number, max: number): boolean {
    return this.hostility >= min && this.hostility <= max;
  }

  // Special dialog management for winning condition
  public checkChoiceRequirements(choice: DialogChoice): boolean {
    if (!choice.requirements) return true;

    const req = choice.requirements;

    // Check affection requirements
    if (req.minAffection !== undefined && this.affection < req.minAffection) return false;
    if (req.maxAffection !== undefined && this.affection > req.maxAffection) return false;

    // Check hostility requirements
    if (req.minHostility !== undefined && this.hostility < req.minHostility) return false;
    if (req.maxHostility !== undefined && this.hostility > req.maxHostility) return false;

    // Check total points requirements
    const totalPoints = this.getTotalPoints();
    if (req.minTotalPoints !== undefined && totalPoints < req.minTotalPoints) return false;
    if (req.maxTotalPoints !== undefined && totalPoints > req.maxTotalPoints) return false;

    // Check item requirements
    if (req.requiredItems) {
      for (const itemReq of req.requiredItems) {
        if (!this.hasItem(itemReq.itemId, itemReq.quantity)) {
          return false;
        }
      }
    }

    // Check state requirements
    if (req.requiredStates) {
      for (const state of req.requiredStates) {
        if (!this.checkStateExists(state)) {
          return false;
        }
      }
    }

    if (req.hideOnstates) {
      for (const state of req.hideOnstates) {
        if (this.checkStateExists(state)) {
          return false;
        }
      }
    }

    if (req.maxRequiredValue) {
      for (const param of req.maxRequiredValue) {
        if (this.checkCustomValue(param.key) > param.value) {
          return false;
        }
      }
    }

    if (req.minRequiredValue) {
      for (const param of req.minRequiredValue) {
        if (this.checkCustomValue(param.key) < param.value) {
          return false;
        }
      }
    }

    return true;
  }

  public unlockSpecialDialog(dialogId: string, npcName: string, specialType?: string): void {
    const key = `${npcName}:${dialogId}`;
    this.unlockedSpecialDialogs.add(key);
    console.log(`Special dialog unlocked: ${key} (${specialType || 'unknown type'})`);

    // Notify listeners about special dialog unlock
    this.notifyListeners('specialDialogUnlocked', this.unlockedSpecialDialogs.size);
  }

  public hasUnlockedSpecialDialog(dialogId: string, npcName: string): boolean {
    const key = `${npcName}:${dialogId}`;
    return this.unlockedSpecialDialogs.has(key);
  }

  public getUnlockedSpecialDialogs(): string[] {
    return Array.from(this.unlockedSpecialDialogs);
  }

  public getSpecialDialogCount(): number {
    return this.unlockedSpecialDialogs.size;
  }

  // Inventory management methods
  public addItem(itemId: string, quantity: number = 1): void {
    const currentQuantity = this.inventory.get(itemId) || 0;
    const newQuantity = currentQuantity + quantity;
    this.inventory.set(itemId, newQuantity);
    
    // Mark as discovered globally
    if (!this.discoveredItems.has(itemId)) {
        this.discoveredItems.add(itemId);
        this.saveDiscoveredItems();
        console.log(`New item discovered: ${itemId}`);
    }

    // Mark as discovered this run
    this.itemsDiscoveredThisRun.add(itemId);

    let itemConfig = getItemConfig(itemId)
    console.log(`Added ${quantity}x ${itemId}. Total: ${newQuantity}`);
    this.notifyInventoryListeners('inventoryChanged');
    this.notifyInventoryListeners('showInventory', itemConfig);
  }

  public removeItem(itemId: string, quantity: number = 1): boolean {
    const currentQuantity = this.inventory.get(itemId) || 0;
    if (currentQuantity < quantity) {
      console.warn(`Cannot remove ${quantity}x ${itemId}. Only have ${currentQuantity}`);
      return false;
    }
    const newQuantity = currentQuantity - quantity;
    if (newQuantity === 0) {
      this.inventory.delete(itemId);
    } else {
      this.inventory.set(itemId, newQuantity);
    }
    console.log(`Removed ${quantity}x ${itemId}. Remaining: ${newQuantity}`);
    this.notifyInventoryListeners('inventoryChanged');
    return true;
  }

  public hasItem(itemId: string, minQuantity: number = 1): boolean {
    const currentQuantity = this.inventory.get(itemId) || 0;
    return currentQuantity >= minQuantity;
  }

  public getItemQuantity(itemId: string): number {
    return this.inventory.get(itemId) || 0;
  }

  public getInventory(): Map<string, number> {
    return new Map(this.inventory);
  }

  public clearInventory(): void {
    this.inventory.clear();
    console.log('Inventory cleared');
    this.notifyInventoryListeners('inventoryChanged');
  }

  // Inventory event system
  public addInventoryEventListener(event: string, callback: () => void): void {
    if (!this.inventoryEventListeners.has(event)) {
      this.inventoryEventListeners.set(event, []);
    }
    const listeners = this.inventoryEventListeners.get(event);
    if (listeners) {
      listeners.push(callback);
    }
  }

  public removeInventoryEventListener(event: string, callback: () => void): void {
    const listeners = this.inventoryEventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private notifyInventoryListeners(event: string, itemConfig?:ItemConfig): void {
    const listeners = this.inventoryEventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        callback(itemConfig);
      });
    }
  }

  // Discovered Items Management
  private loadDiscoveredItems(): void {
    try {
        const saved = localStorage.getItem(this.DISCOVERED_ITEMS_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed)) {
                this.discoveredItems = new Set(parsed);
                console.log(`Loaded ${this.discoveredItems.size} discovered items from localStorage.`);
            }
        }
    } catch (e) {
        console.error('Failed to load discovered items:', e);
    }
  }

  private saveDiscoveredItems(): void {
    try {
        const array = Array.from(this.discoveredItems);
        localStorage.setItem(this.DISCOVERED_ITEMS_KEY, JSON.stringify(array));
    } catch (e) {
        console.error('Failed to save discovered items:', e);
    }
  }

  public getDiscoveredItems(): Set<string> {
      return new Set(this.discoveredItems);
  }

  public isItemDiscovered(itemId: string): boolean {
      return this.discoveredItems.has(itemId);
  }

  // NPC interaction tracking methods
  public markNPCInteraction(
    npcName: string,
    dialogNodeId: string,
    speaker: string,
    text: string,
    characterImage?: string,
    backgroundImage?: string,
    isEnd?: boolean,
    choices?: Array<{ text: string; wasSelected: boolean }>,
  ): void {
    const currentTime = Date.now();
    const isRepeatable = this.repeatableNPCs.has(npcName);
    const existing = this.npcInteractionStates.get(npcName);

    if (existing) {
      // Update existing interaction
      existing.lastDialogNodeId = dialogNodeId;
      existing.lastSpeaker = speaker;
      existing.lastText = text;
      existing.lastCharacterImage = characterImage;
      existing.lastBackgroundImage = backgroundImage;
      existing.lastIsEnd = isEnd;
      existing.lastChoices = choices;
      existing.interactionCount++;
      existing.lastInteractionTime = currentTime;
      existing.isRepeatable = isRepeatable;
    } else {
      // Create new interaction state
      this.npcInteractionStates.set(npcName, {
        hasInteracted: true,
        lastDialogNodeId: dialogNodeId,
        lastSpeaker: speaker,
        lastText: text,
        lastCharacterImage: characterImage,
        lastBackgroundImage: backgroundImage,
        lastIsEnd: isEnd,
        lastChoices: choices,
        interactionCount: 1,
        firstInteractionTime: currentTime,
        lastInteractionTime: currentTime,
        isRepeatable: isRepeatable,
      });
    }

    console.log(
      `NPC interaction recorded: ${npcName} - ${dialogNodeId}${isRepeatable ? ' (repeatable)' : ''}`,
    );
  }

  public hasInteractedWithNPC(npcName: string): boolean {
    const state = this.npcInteractionStates.get(npcName);
    const isRepeatable = this.repeatableNPCs.has(npcName);

    // For repeatable NPCs, always return false so they can be interacted with again
    if (isRepeatable) {
      return false;
    }

    return state?.hasInteracted ?? false;
  }

  public getNPCInteractionState(npcName: string): NPCInteractionState | null {
    return this.npcInteractionStates.get(npcName) ?? null;
  }

  public getAllNPCInteractionStates(): Map<string, NPCInteractionState> {
    return new Map(this.npcInteractionStates);
  }

  public resetNPCInteraction(npcName: string): void {
    this.npcInteractionStates.delete(npcName);
    console.log(`NPC interaction reset: ${npcName}`);
  }

  public resetAllNPCInteractions(): void {
    this.npcInteractionStates.clear();
    console.log('All NPC interactions reset');
  }

  public resetNonRepeatableNPCInteractions(): void {
    const keysToDelete: string[] = [];

    for (const [npcName] of this.npcInteractionStates.entries()) {
      if (!this.repeatableNPCs.has(npcName)) {
        keysToDelete.push(npcName);
      }
    }

    for (const key of keysToDelete) {
      this.npcInteractionStates.delete(key);
    }

    console.log(`Reset interactions for non-repeatable NPCs: ${keysToDelete.join(', ')}`);
  }

  public getLastDialogNode(
    npcName: string,
  ): { speaker: string; text: string; nodeId: string } | null {
    const state = this.npcInteractionStates.get(npcName);
    const isRepeatable = this.repeatableNPCs.has(npcName);

    // For repeatable NPCs, don't return last dialog to force fresh interactions
    if (isRepeatable) {
      return null;
    }

    if (!state || !state.lastDialogNodeId || !state.lastSpeaker || !state.lastText) {
      return null;
    }

    return {
      speaker: state.lastSpeaker,
      text: state.lastText,
      nodeId: state.lastDialogNodeId,
    };
  }

  // Methods to manage repeatable NPCs
  public addRepeatableNPC(npcName: string): void {
    this.repeatableNPCs.add(npcName);
    console.log(`Added ${npcName} to repeatable NPCs list`);
  }

  public removeRepeatableNPC(npcName: string): void {
    this.repeatableNPCs.delete(npcName);
    console.log(`Removed ${npcName} from repeatable NPCs list`);
  }

  public isRepeatableNPC(npcName: string): boolean {
    return this.repeatableNPCs.has(npcName);
  }

  public getRepeatableNPCs(): string[] {
    return Array.from(this.repeatableNPCs);
  }

  public setRepeatableNPCs(npcNames: string[]): void {
    this.repeatableNPCs = new Set(npcNames);
    console.log(`Set repeatable NPCs: ${npcNames.join(', ')}`);
  }

  // Save/load functionality for future use
  public getState(): {
    points: number;
    affection: number;
    hostility: number;
    currentDay: number;
    npcInteractionStates: [string, NPCInteractionState][];
    unlockedSpecialDialogs: string[];
    repeatableNPCs: string[];
    dayDialogShown: number[];
    inventory: [string, number][];
    itemsDiscoveredThisRun?: string[];
  } {
    return {
      points: this.getTotalPoints(), // For backward compatibility
      affection: this.affection,
      hostility: this.hostility,
      currentDay: this.currentDay,
      npcInteractionStates: Array.from(this.npcInteractionStates.entries()),
      unlockedSpecialDialogs: Array.from(this.unlockedSpecialDialogs),
      repeatableNPCs: Array.from(this.repeatableNPCs),
      dayDialogShown: Array.from(this.dayDialogShown),
      inventory: Array.from(this.inventory.entries()),
      itemsDiscoveredThisRun: Array.from(this.itemsDiscoveredThisRun),
    };
  }

  public loadState(state: {
    points?: number;
    affection?: number;
    hostility?: number;
    currentDay?: number;
    npcInteractionStates?: [string, NPCInteractionState][];
    unlockedSpecialDialogs?: string[];
    repeatableNPCs?: string[];
    dayDialogShown?: number[];
    inventory?: [string, number][];
    itemsDiscoveredThisRun?: string[];
  }): void {
    // Load affection and hostility if available
    if (state.affection !== undefined) {
      this.affection = state.affection;
    }
    if (state.hostility !== undefined) {
      this.hostility = state.hostility;
    }

    // Load unlocked special dialogs
    if (state.unlockedSpecialDialogs) {
      this.unlockedSpecialDialogs = new Set(state.unlockedSpecialDialogs);
    }

    // Load repeatable NPCs if available
    if (state.repeatableNPCs) {
      this.repeatableNPCs = new Set(state.repeatableNPCs);
    }

    // Load day dialog shown flags if available
    if (state.dayDialogShown) {
      this.dayDialogShown = new Set(state.dayDialogShown);
    }

    // Load inventory if available
    if (state.inventory) {
      this.inventory = new Map(state.inventory);
      console.log(`Inventory loaded with ${this.inventory.size} item types`);

      // Initialize itemsDiscoveredThisRun from inventory as a fallback
      for (const [itemId] of this.inventory) {
        this.itemsDiscoveredThisRun.add(itemId);
      }
    }

    // Load items discovered this run if available
    if (state.itemsDiscoveredThisRun) {
      this.itemsDiscoveredThisRun = new Set(state.itemsDiscoveredThisRun);
    }

    // Fallback to old points system for backward compatibility
    if (
      state.points !== undefined &&
      state.affection === undefined &&
      state.hostility === undefined
    ) {
      this.setPoints(state.points);
    }

    console.log(
      `State loaded - Affection: ${this.affection}, Hostility: ${this.hostility}, Total: ${this.getTotalPoints()}`,
    );

    // Load current day if available
    if (
      state.currentDay !== undefined &&
      state.currentDay >= 1 &&
      state.currentDay <= dayConfigs.length
    ) {
      this.currentDay = state.currentDay;
    }

    // Load NPC interaction states if available (but they will be reset when day is set)
    if (state.npcInteractionStates) {
      this.npcInteractionStates = new Map(state.npcInteractionStates);
    }

    // Reinitialize NPCs for the loaded day and reset interactions for non-repeatable NPCs
    this.initializeNPCs();
    this.resetNonRepeatableNPCInteractions();

    this.notifyListeners('affectionChanged', this.affection);
    this.notifyListeners('hostilityChanged', this.hostility);
    this.notifyListeners('pointsChanged', this.getTotalPoints());
    this.notifyDayListeners('dayChanged', this.currentDay);
  }

  // Day system methods
  public getCurrentDay(): number {
    return this.currentDay;
  }

  public getCurrentDayConfig(): DayConfig | null {
    return dayConfigs.find(config => config.day === this.currentDay) || null;
  }

  /**
   * Get the current story progress value
   */
  public getCurrentProgress(): number {
    const dayConfig = this.getCurrentDayConfig();
    if (dayConfig?.progress !== undefined) {
      return dayConfig.progress;
    }
    return (this.currentDay - 1) / (dayConfigs.length - 1);
  }

  public getDayProgress(day: number): number {
    const dayConfig = dayConfigs.find(config => config.day === day);
    if (dayConfig?.progress !== undefined) {
      return dayConfig.progress;
    }
    return (day - 1) / (dayConfigs.length - 1);
  }

  public setDay(day: number): void {
    if (day < 1 || day > dayConfigs.length) {
      console.warn(`Invalid day: ${day}. Must be between 1 and ${dayConfigs.length}`);
      return;
    }

    const oldDay = this.currentDay;
    this.currentDay = day;

    // Reinitialize NPCs with the new day's configuration
    this.initializeNPCs();

    // Clear interaction states when changing days for non-repeatable NPCs only
    // This allows re-interaction with regular NPCs but preserves repeatable NPC state
    this.resetNonRepeatableNPCInteractions();

    // Check if there's a teleport position defined for this day and teleport the player if available
    const dayConfig = this.getCurrentDayConfig();
    if (dayConfig?.position && this.player) {
      console.log(
        `Teleporting player to position: x=${dayConfig.position.x}, y=${dayConfig.position.y}`,
      );
      this.player.setPosition(dayConfig.position.x, dayConfig.position.y);
    }
    
    // Set player facing direction if configured
    if (dayConfig?.facing && this.player) {
      console.log(`Setting player facing direction to: ${dayConfig.facing}`);
      this.player.setDirection(dayConfig.facing);
    }

    console.log(`Day changed from ${oldDay} to ${day}`);

    // Play day-specific music if configured
    if (dayConfig?.music) {
      const soundManager = SoundManager.getInstance();
      // Add 'music_' prefix to music ID when playing
      soundManager.playMusic(`music_${dayConfig.music}`);
      console.log(`Playing day music: ${dayConfig.music}`);
    } else {
      console.log('No music configured for this day');
    }

    this.notifyDayListeners('dayChanged', day);

    const progress = this.getCurrentProgress();
    this.notifyListeners('progressChanged', progress);
    console.log(`Story progress: ${(progress * 100).toFixed(1)}%`);
  }

  public getDayAsset(whatDay:number){
    let npcConfig = this.getDayInfo(whatDay)?.npcs
    let assets:any[] = []
    let initialDialog = this.getDayInfo(whatDay)?.initialDialog?.nodes

    if(initialDialog){
      for(let dialogKey of Object.keys(initialDialog)){
        let dialog = initialDialog[dialogKey]
        if(!!dialog.backgroundImage && !assets.includes(dialog.backgroundImage)){
          assets.push(dialog.backgroundImage)
        }
      }
    }

    for(let npc of npcConfig){
      for(let dialogKey of Object.keys(npc.dialogTree["nodes"])){
        let dialog = npc.dialogTree["nodes"][dialogKey]
        if(!assets.includes(dialog.backgroundImage) && !!dialog.backgroundImage){
          assets.push(dialog.backgroundImage)
        }
      }
    }

    return assets

  }

  public nextDay(howManyDays?:number): boolean {
    if (this.currentDay < dayConfigs.length) {
      this.setDay(this.currentDay + (howManyDays ? howManyDays : 1));
      this.loadDayAsset(this.currentDay+1) //Load tommorow's asset
      return true;
    }
    return false;
  }

  public loadDayAsset(dayIndex:number){
    let assetManager = AssetManager.getInstance();
    let assets = this.getDayAsset(dayIndex)
    assetManager.loadDailyImageAssetByAssetPath(assets)
  }

  public previousDay(): boolean {
    if (this.currentDay > 1) {
      this.setDay(this.currentDay - 1);
      return true;
    }
    return false;
  }

  public getTotalDays(): number {
    return dayConfigs.length;
  }

  public getDayInfo(day?: number): DayConfig | null {
    const targetDay = day ?? this.currentDay;
    return dayConfigs.find(config => config.day === targetDay) || null;
  }

  public getAllDayConfigs(): DayConfig[] {
    return [...dayConfigs];
  }

  // Initial day dialog management
  public shouldShowInitialDayDialog(day?: number): boolean {
    const targetDay = day ?? this.currentDay;
    const hasShown = this.dayDialogShown.has(targetDay);
    console.log(
      `shouldShowInitialDayDialog for day ${targetDay}: hasShown=${hasShown}, result=${!hasShown}`,
    );
    return !hasShown;
  }

  public getInitialDayDialog(day?: number): DialogTree | null {
    const targetDay = day ?? this.currentDay;
    const dayConfig = dayConfigs.find(config => config.day === targetDay);
    const dialog = dayConfig?.initialDialog || null;
    console.log(
      `getInitialDayDialog for day ${targetDay}: found config=${!!dayConfig}, has dialog=${!!dialog}`,
    );
    return dialog;
  }

  public markInitialDayDialogShown(day?: number): void {
    const targetDay = day ?? this.currentDay;
    this.dayDialogShown.add(targetDay);
    console.log(`Initial dialog marked as shown for day ${targetDay}`);
  }

  public resetInitialDayDialogFlags(): void {
    this.dayDialogShown.clear();
    console.log('All initial day dialog flags reset');
  }

  // Day event system
  public addDayEventListener(event: string, callback: (day: number) => void): void {
    if (!this.dayEventListeners.has(event)) {
      this.dayEventListeners.set(event, []);
    }
    const listeners = this.dayEventListeners.get(event);
    if (listeners) {
      listeners.push(callback);
    }
  }

  public removeDayEventListener(event: string, callback: (day: number) => void): void {
    const listeners = this.dayEventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private notifyDayListeners(event: string, day: number): void {
    const listeners = this.dayEventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        callback(day);
      });
    }
  }

  // NPC Management Methods (updated to use current day)
  public initializeNPCs(): NPC[] {
    // Clear existing NPCs array
    this.npcs = [];

    // Get current day configuration
    const currentDayConfig = this.getCurrentDayConfig();
    if (!currentDayConfig) {
      console.warn(`No configuration found for day ${this.currentDay}, using day 1 as fallback`);
      // Fallback to day 1 or use legacy npcConfigs
      const fallbackConfig = dayConfigs[0] || { day: 1, name: 'Default', npcs: npcConfigs };
      for (const npcConfig of fallbackConfig.npcs) {
        this.npcs.push(new NPC(npcConfig));
      }
    } else {
      // Create NPCs from current day configuration
      for (const npcConfig of currentDayConfig.npcs) {
        this.npcs.push(new NPC(npcConfig));
      }
    }

    console.log(`Initialized ${this.npcs.length} NPCs for day ${this.currentDay}`);
    return this.npcs;
  }

  public getNPCs(): NPC[] {
    return this.npcs;
  }

  // Methods to manage the player reference
  public setPlayer(player: Player): void {
    this.player = player;
    console.log('Player reference set in StoryManager');
  }

  public getPlayer(): Player | null {
    return this.player;
  }

  public updateNPCs(
    deltaTime: number,
    playerPosition: { x: number; y: number },
  ): {
    playerNearNPC: boolean;
    closestNPC: NPC | null;
  } {
    let playerNearNPC = false;
    let closestNPC: NPC | null = null;
    let closestDistance = Infinity;

    // Get player collider for accurate triggerOnCollision detection
    const playerCollider = this.player?.getCollider();

    // First pass: update all NPCs and find closest
    for (const npc of this.npcs) {
      npc.update(deltaTime, playerPosition, playerCollider);

      if (npc.canInteract(playerPosition)) {
        playerNearNPC = true;

        // Calculate distance to find closest NPC
        const dx = playerPosition.x - npc.getPosition().x;
        const dy = playerPosition.y - npc.getPosition().y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestNPC = npc;
        }
      }
    }

    // Second pass: set focus only on the closest NPC
    for (const npc of this.npcs) {
      npc.setFocused(npc === closestNPC);
    }

    return { playerNearNPC, closestNPC };
  }

  public handleNPCInteraction(playerPosition: { x: number; y: number }):
    | DialogTree
    | {
        type: 'repeat';
        speaker: string;
        text: string;
        characterImage?: string;
        backgroundImage?: string;
        isEnd?: boolean;
      }
    | null {
    // Check if player is near any focused NPC
    for (const npc of this.npcs) {
      if (npc.canInteract(playerPosition) && npc.getFocused()) {
        return npc.interact();
      }
    }
    return null;
  }

  public setNPCCollidersVisible(visible: boolean): void {
    for (const npc of this.npcs) {
      npc.setColliderVisible(visible);
    }
  }

  //soleh CHECKED
  //playboy
  //cerdik
  //lamban
  //klepto CHECKED
  //pengecut CHECEKD
  //pendendam
  //beruntung

  private EPITHETS = [
    {
      name : "Soleh",
      requirement : (_gameStates:any, _gameValues:any) =>{

        let count = 0
        if(_gameStates.includes("BONEKA_GIVEN")){
          count = count + 1;
        }
        if(_gameStates.includes("MONEY_NOT_TAKEN")){
          count = count + 1;
        }
        if(_gameStates.includes("DONATED_TO_CHURCH")){
          count = count + 1;
        }

        return count > 1
      }
    },
    {
      name : "Playboy",
      requirement : (_gameStates:any, _gameValues:any) =>{
        return _gameValues.elsa_affinity > 4 && _gameValues.bunga_affinity > 2
      }
    },
    {
      name : "Cerdik",
      requirement : (_gameStates:any, _gameValues:any) =>{
        return !(!_gameValues.false_arguments || _gameValues.false_arguments > 0) && (_gameStates.includes( CUSTOM_GAME_STATE.ENDING_S_SECRET_ENDING+"") || _gameStates.includes(CUSTOM_GAME_STATE.ENDING_A_GOOD_ENDING+""));
      }
    },
    {
      name : "Lamban",
      requirement : (_gameStates:any, _gameValues:any) =>{
        return (_gameValues.false_arguments > 2);//TODO make "stupid arguments"
      }
    },
    {
      name : "Pengecut",
      requirement : (_gameStates:any, _gameValues:any) =>{
        return _gameStates.includes( CUSTOM_GAME_STATE.ENDING_F_GAME_OVER_ENDING+"")
      }
    },
    {
      name : "Pendendam",
      requirement : (_gameStates:any, _gameValues:any) =>{
        return true //todo tambahin semacem "vengeance points?"
      }
    },
    {
      name : "Kleptomaniac",
      requirement : (_gameStates:any, _gameValues:any) =>{
        return (!_gameStates.includes('BONEKA_GIVEN') && _gameStates.includes('BEAR_RETRIEVED')) && _gameStates.includes('MONEY_TAKEN')
      }
    },
    {
      name : "Beruntung",
      requirement : (_gameStates:any, _gameValues:any) =>{
        return true //todo tambahin value lucky answers == 3, itu dari yang dia lompat pager, terus apa lagi
      }
    },
  ]

  private getFinalEpithet(){
    let filteredEpithets = this.EPITHETS.filter(epithet=>{
      return epithet.requirement(this.gameStates, this.customValues)
    }).map(epithet=>{return epithet.name})

    if(filteredEpithets.length === 0){
      filteredEpithets.push("Penonton")
    }

    console.log("PSBE", this.gameStates, this.customValues)
    console.log("PSBE", filteredEpithets)

    let randomNumber = Math.floor(Math.random() * 3)
    return filteredEpithets[randomNumber]

  }

  public getFinalReport(){

    const endTime = performance.now();
    const timeElapsedMS = endTime - this.startTime;
    let duration = (timeElapsedMS / 1000).toFixed(2);//in seconds

    let itemCount = this.itemsDiscoveredThisRun.size;

    const includedSecret = [
      CUSTOM_GAME_STATE.BONEKA_GIVEN,
      CUSTOM_GAME_STATE.ATO_HAPPY,
      CUSTOM_GAME_STATE.HOLGA_FOUND_MORNING,
      CUSTOM_GAME_STATE.ROD_USED,
      CUSTOM_GAME_STATE.DIANA_BIRTHDAY_HAPPY,
      CUSTOM_GAME_STATE.EVIDENCE_CLOTH_GATE_FOUND,
      CUSTOM_GAME_STATE.BUNGA_BATH,
      CUSTOM_GAME_STATE.HOLGA_PANADA_POISONED,
    ]

    let secretCount = this.gameStates.filter(obj=>obj===includedSecret+"").length
    let secretPercentage = Math.floor(secretCount / includedSecret.length * 100)

    let endingScore = 0;

    const POSSIBLE_ENDING_GAME_STATE = [
      CUSTOM_GAME_STATE.ENDING_S_SECRET_ENDING,
      CUSTOM_GAME_STATE.ENDING_A_GOOD_ENDING,
      CUSTOM_GAME_STATE.ENDING_B_STILL_SURVIVE_ENDING,
      CUSTOM_GAME_STATE.ENDING_D_FAIL_SELF_OR_ELSA_ENDING,
      CUSTOM_GAME_STATE.ENDING_F_GAME_OVER_ENDING,
    ]

    console.log("GIOX STATES", this.gameStates)

    let endingGrade = this.gameStates.find(obj=>POSSIBLE_ENDING_GAME_STATE.find(obj2=>obj2+""===obj))

    if(endingGrade === CUSTOM_GAME_STATE.ENDING_S_SECRET_ENDING+""){
      endingScore = 100
    }
    if(endingGrade === CUSTOM_GAME_STATE.ENDING_A_GOOD_ENDING+""){
      endingScore = 80
    }
    if(endingGrade === CUSTOM_GAME_STATE.ENDING_B_STILL_SURVIVE_ENDING+""){
      endingScore = 60
    }
    if(endingGrade === CUSTOM_GAME_STATE.ENDING_D_FAIL_SELF_OR_ELSA_ENDING+""){
      endingScore = 20
    }
    if(endingGrade === CUSTOM_GAME_STATE.ENDING_F_GAME_OVER_ENDING+""){
      endingScore = 0
    }

    let generalKarmaRaw = this.customValues.general_karma ? this.customValues.general_karma : 0
    let generalKarmaScore = generalKarmaRaw+6/12 * 100

    let bungaAffinityRaw = this.customValues.general_karma ? this.customValues.general_karma : 0
    let bungaAffinityScore = bungaAffinityRaw+4/8 * 100

    let holgaAffinityRaw = this.customValues.holga_affinity ? this.customValues.holga_affinity : 0
    let holgaAffinityScore = holgaAffinityRaw+2/5 * 100

    let atoAffinityRaw = this.customValues.ato_affinity ? this.customValues.ato_affinity : 0
    let atoAffinityScore = atoAffinityRaw/2 * 100

    let elsaAffinityRaw = this.customValues.elsa_affinity ? this.customValues.elsa_affinity : 0
    let elsaAffinityScore = elsaAffinityRaw+3/10 * 100

    let affinityTotal = generalKarmaScore + bungaAffinityScore + holgaAffinityScore + atoAffinityScore + elsaAffinityScore
    let affinityScore = affinityTotal / 5

    let itemScore = itemCount / 6 * 100
    if(itemScore > 100) itemScore = 100

    let finalScore = (itemScore + secretPercentage + affinityScore + endingScore)/4;
    let finalRank = finalScore > 90 ? "A" : finalScore > 75 ? "B" : finalScore > 60 ? "C" : finalScore > 50 ? "D" : "F"

    let finalPersonality = this.getFinalEpithet()

    console.log("PSBE", finalPersonality)


    return {
      duration,
      itemCount,
      secretCount,
      endingGrade : endingGrade ?? "F",
      finalRank,
      finalPersonality,
      itemScore, secretPercentage, affinityScore, endingScore
    }

  }

}
